import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { ClipboardList } from "lucide-react";
import { useScorecardCategories, useScorecardFields } from "@/hooks/scorecard/useScorecardConfig";
import {
	useCandidateScorecard,
	useEvaluatorName,
	useSaveCandidateScorecard,
} from "@/hooks/scorecard/useCandidateScorecard";
import { useBeforeUnloadGuard, useScorecardDraft } from "@/hooks/scorecard/useScorecardDraft";
import type { ScorecardData, ScorecardField, ScorecardValue } from "@/types/scorecard";
import { ScorecardCategorySection } from "./ScorecardCategorySection";
import { ScorecardHeader } from "./ScorecardHeader";
import { ScorecardSkeleton } from "./ScorecardSkeleton";
import { isFieldCompleted } from "./scorecardUtils";

interface CandidateScorecardTabProps {
	candidateId: string;
}

interface ScorecardSectionData {
	key: string;
	title: string;
	description: string | null;
	color: string | null;
	fields: ScorecardField[];
}

/**
 * Candidate evaluation tab: renders the organization's configured scorecard
 * grouped by category, with draft persistence and progress tracking.
 * Answers are stored on the candidate row keyed by field id.
 */
export const CandidateScorecardTab: React.FC<CandidateScorecardTabProps> = ({ candidateId }) => {
	const { t } = useTranslation();
	const { toast } = useToast();
	const { currentOrganization, user } = useAuth();
	const organizationId = currentOrganization?.id;

	const fieldsQuery = useScorecardFields(organizationId);
	const categoriesQuery = useScorecardCategories(organizationId);
	const recordQuery = useCandidateScorecard(candidateId, organizationId);
	const saveMutation = useSaveCandidateScorecard(candidateId, organizationId);
	const evaluatorQuery = useEvaluatorName(recordQuery.data?.scorecard_completed_by);
	const { readDraft, saveDraft, clearDraft } = useScorecardDraft(candidateId, organizationId);

	const [values, setValues] = useState<ScorecardData>({});
	const [hasChanges, setHasChanges] = useState(false);
	const [showRequiredHints, setShowRequiredHints] = useState(false);
	// Re-initialize when switching candidate, not on every background refetch.
	const initializedForRef = useRef<string | null>(null);

	useBeforeUnloadGuard(hasChanges);

	useEffect(() => {
		if (recordQuery.data === undefined || initializedForRef.current === candidateId) return;
		initializedForRef.current = candidateId;
		setShowRequiredHints(false);
		const saved = recordQuery.data?.scorecard_data ?? {};
		const draft = readDraft();
		const savedAt = recordQuery.data?.scorecard_updated_at
			? new Date(recordQuery.data.scorecard_updated_at).getTime()
			: 0;
		if (draft && draft.timestamp > savedAt) {
			setValues({ ...saved, ...draft.data });
			setHasChanges(true);
			toast({
				title: t("candidates.tabsContent.scorecard.draftRestored.title", "Draft restored"),
				description: t(
					"candidates.tabsContent.scorecard.draftRestored.description",
					"Unsaved changes from a previous session were restored.",
				),
			});
		} else {
			if (draft) clearDraft();
			setValues(saved);
			setHasChanges(false);
		}
	}, [recordQuery.data, candidateId, readDraft, clearDraft, toast, t]);

	const sections = useMemo<ScorecardSectionData[]>(() => {
		const fields = fieldsQuery.fields;
		const grouped: ScorecardSectionData[] = categoriesQuery.categories
			.map((category) => ({
				key: category.id,
				title: category.name,
				description: category.description,
				color: category.color,
				fields: fields.filter((field) => field.category_id === category.id),
			}))
			.filter((section) => section.fields.length > 0);
		const uncategorized = fields.filter(
			(field) =>
				!field.category_id ||
				!categoriesQuery.categories.some((category) => category.id === field.category_id),
		);
		if (uncategorized.length > 0) {
			grouped.push({
				key: "uncategorized",
				title: t("candidates.tabsContent.scorecard.uncategorized", "General"),
				description: null,
				color: null,
				fields: uncategorized,
			});
		}
		return grouped;
	}, [fieldsQuery.fields, categoriesQuery.categories, t]);

	const progress = useMemo(() => {
		const fields = fieldsQuery.fields;
		const requiredFields = fields.filter((field) => field.required);
		const completed = fields.filter((field) => isFieldCompleted(field, values[field.id]));
		const requiredCompleted = requiredFields.filter((field) =>
			isFieldCompleted(field, values[field.id]),
		);
		return {
			overall: fields.length ? Math.round((completed.length / fields.length) * 100) : 0,
			required: requiredFields.length
				? Math.round((requiredCompleted.length / requiredFields.length) * 100)
				: null,
			missingRequired: requiredFields.length - requiredCompleted.length,
		};
	}, [fieldsQuery.fields, values]);

	const handleChange = (fieldId: string, value: ScorecardValue) => {
		setValues((previous) => {
			const next = { ...previous, [fieldId]: value };
			saveDraft(next);
			return next;
		});
		setHasChanges(true);
	};

	const handleSave = async () => {
		if (!user?.id || saveMutation.isPending) return;
		if (progress.missingRequired > 0) {
			setShowRequiredHints(true);
			toast({
				title: t(
					"candidates.tabsContent.scorecard.requiredMissingWarning",
					"{{count}} required field(s) are still empty.",
					{ count: progress.missingRequired },
				),
			});
		}
		try {
			await saveMutation.mutateAsync({ data: values, completedBy: user.id });
			clearDraft();
			setHasChanges(false);
			toast({ title: t("candidates.tabsContent.scorecard.toasts.saved", "Scorecard saved") });
		} catch {
			toast({
				title: t(
					"candidates.tabsContent.scorecard.errors.saveFailed",
					"Could not save the scorecard",
				),
				description: t("candidates.tabsContent.scorecard.errors.tryAgain", "Please try again."),
				variant: "destructive",
			});
		}
	};

	const isLoading =
		fieldsQuery.isLoading || categoriesQuery.isLoading || recordQuery.isLoading;
	const isError = fieldsQuery.isError || categoriesQuery.isError || recordQuery.isError;

	if (isLoading) return <ScorecardSkeleton />;

	if (isError) {
		return (
			<div className="flex flex-col items-center gap-3 py-12 text-center">
				<p className="text-sm text-muted-foreground">
					{t(
						"candidates.tabsContent.scorecard.errors.loadFailed",
						"The scorecard could not be loaded.",
					)}
				</p>
				<Button
					variant="outline"
					onClick={() => {
						fieldsQuery.refetch();
						categoriesQuery.refetch();
						recordQuery.refetch();
					}}
				>
					{t("candidates.tabsContent.scorecard.errors.retry", "Retry")}
				</Button>
			</div>
		);
	}

	if (fieldsQuery.fields.length === 0) {
		return (
			<EmptyState
				icon={ClipboardList}
				title={t("candidates.tabsContent.scorecard.empty.title", "No scorecard configured")}
				description={t(
					"candidates.tabsContent.scorecard.empty.description",
					"An administrator can define scorecard fields in the Admin Console.",
				)}
			/>
		);
	}

	return (
		<div>
			<ScorecardHeader
				overallProgress={progress.overall}
				requiredProgress={progress.required}
				hasChanges={hasChanges}
				saving={saveMutation.isPending}
				onSave={handleSave}
				lastSavedAt={recordQuery.data?.scorecard_completed_at ?? null}
				lastSavedByName={evaluatorQuery.data ?? null}
			/>
			<div className="space-y-4">
				{sections.map((section) => (
					<ScorecardCategorySection
						key={section.key}
						title={section.title}
						description={section.description}
						color={section.color}
						fields={section.fields}
						values={values}
						onChange={handleChange}
						showRequiredHints={showRequiredHints}
					/>
				))}
			</div>
		</div>
	);
};
